import { useMemo } from 'react';
import {Track} from '@app/web-player/tracks/track';

import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';

import clsx from 'clsx';
import { MusicNoteIcon } from '@ui/icons/material/MusicNote';


interface TrackImageProps {
    track: Track;
    className?: string;
    size?: string;
    background?: string;
}
export function TrackImage(
    {
        track,
        className,
        size,
        background = 'bg-fg-base/4',
    }: TrackImageProps
) {
    const { trans } = useTrans();

    const src = useMemo(() => {
        const image = getTrackImageSrc(track)

        if (!image) return '';

        // Check if we should use thumbnail based on size
        const shouldUseThumbnail = () => {
            if (image?.includes('scdn')) {
                return false
            }

            if (!image?.endsWith('.webp')) {
                return false
            }

            const allClasses = `${className || ''} ${size || ''}`;

            // Extract width and height classes
            const widthMatch = allClasses.match(/w-(\d+)/);
            const heightMatch = allClasses.match(/h-(\d+)/);

            const width = widthMatch ? parseInt(widthMatch[1]) : null;
            const height = heightMatch ? parseInt(heightMatch[1]) : null;

            // Use thumbnail if width < 200 or height < 200
            return (width !== null && width < 200) || (height !== null && height < 200);
        };

        if (!shouldUseThumbnail()) {
            return image;
        }

        // Split the URL to get the path and filename
        const lastSlashIndex = image.lastIndexOf('/');
        const basePath = image.substring(0, lastSlashIndex + 1);
        const filename = image.substring(lastSlashIndex + 1);

        // Add thumbnail prefix to filename
        return `${basePath}thumbnail-${filename}`;
    }, [track])

    const imgClassName = clsx(
        className,
        size,
        background,
        'object-cover',
        !src ? 'flex items-center justify-center' : 'block',
    );
    return src ? (
        <img
            className={imgClassName}
            draggable={false}
            loading="lazy"
            src={src}
            alt={trans(message('Image for :name', {values: {name: track.name}}))}
        />
    ) : (
        <span className={clsx(imgClassName, 'overflow-hidden')}>
      <MusicNoteIcon className="max-w-[60%] text-divider" size="text-9xl" />
    </span>
    );
}

export function getTrackImageSrc(track: Track) {
    if (track.image) {
        return track.image;
    } else if (track.album?.image) {
        return track.album.image;
    }
}
